<template>
    <v-container class="pa-12">
        <v-card class="pl-16 pr-16 pt-8 pb-16">
            <v-card-title>
                <v-row>
                    <v-col cols="6" md="6">
                        <h3>Quotation / ใบเสนอราคา</h3>
                    </v-col>
                    <v-col cols="6" md="6">
                        <v-row>
                            <v-col class="text-right" cols="12" md="12">
                                <h5>No. {{ doc.doc_no }}</h5>
                                <h5>Date {{ doc.doc_date_show }}</h5>
                                <v-chip x-small :color="statusColor" dark class="mt-1">🔒 {{ statusLabel }}</v-chip>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-title>

            <div v-if="doc.doc_type === 'estimate'" class="wf-alert wf-alert-warn">
                ⏱️ เอกสารนี้เป็น <b>การประมาณการค่าบริการเบื้องต้น (Non-binding Estimate)</b>
                อยู่ระหว่างดำเนินการ (In Progress) — ผูก Invoice ได้หลายใบ ดูรายละเอียดด้านล่าง
            </div>

            <!-- ===== Header fields: ล็อกทั้งหมด (read-only) ===== -->
            <v-row class="row1 mt-3 pt-3">
                <v-col cols="12" md="6">
                    <v-text-field v-model="doc.client_code" label="Client Code" outlined dense
                        background-color="#F5F5F5" disabled></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field v-model="doc.client_name" label="Client Name" outlined dense
                        background-color="#F5F5F5" disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="10">
                    <v-text-field v-model="doc.client_address" label="Client Address" outlined dense
                        background-color="#F5F5F5" disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="5">
                    <v-text-field v-model="doc.tax_id" label="Tax ID" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
                <v-col cols="12" sm="5">
                    <v-text-field v-model="doc.branch" label="Branch" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="3">
                    <v-text-field v-model="doc.payment_term" label="Payment Term (Day)" outlined dense
                        background-color="#F5F5F5" disabled></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="doc.currency" label="Currency" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="doc.vat_percent" label="Vat (%)" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
            </v-row>

            <!-- ===== Fixed Price item table (read-only) ===== -->
            <template v-if="doc.doc_type === 'fixed'">
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">รายการสินค้า/บริการ</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter">No.</th>
                                <th class="texttablehcenter" style="min-width:240px">ชื่อสินค้า/รายละเอียด</th>
                                <th class="texttablehcenter text-right" style="width:90px">จำนวน</th>
                                <th class="texttablehcenter" style="width:100px">หน่วย</th>
                                <th class="texttablehcenter text-right" style="width:120px">ราคาต่อหน่วย</th>
                                <th class="texttablehcenter text-right" style="width:120px">ราคารวม</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in doc.items" :key="row.id">
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    {{ row.name }}
                                    <div v-if="row.detail" class="caption blue--text multiline-detail">{{ row.detail }}
                                    </div>
                                </td>
                                <td class="text-right">{{ row.qty }}</td>
                                <td>{{ row.unit }}</td>
                                <td class="text-right">{{ fmt(row.price) }}</td>
                                <td class="text-right">{{ fmt(row.qty * row.price) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>

                <v-row class="heightsize55formadvance mt-2">
                    <v-col class="text-right" cols="12" md="7"> </v-col>
                    <v-col class="text-right" cols="12" md="2">Total</v-col>
                    <v-col class="text-right" cols="12" md="3">
                        <b>{{ fmt(fixedTotals.total) }}</b>
                    </v-col>
                </v-row>
                <v-row class="heightsize55formadvance">
                    <v-col class="text-right" cols="12" md="7"> </v-col>
                    <v-col class="text-right" cols="12" md="2">VAT ({{ doc.vat_percent }}%)</v-col>
                    <v-col class="text-right" cols="12" md="3">
                        <b>{{ fmt(fixedTotals.vat) }}</b>
                    </v-col>
                </v-row>
                <v-row class="heightsize55formadvance">
                    <v-col class="text-right" cols="12" md="7"> </v-col>
                    <v-col class="text-right" cols="12" md="2">Grand total</v-col>
                    <v-col class="text-right" cols="12" md="3">
                        <span class="text-h6"><b>{{ fmt(fixedTotals.grandTotal) }}</b></span>
                    </v-col>
                </v-row>
            </template>

            <!-- ===== Estimate Fee item table (read-only) — ผูก Invoice ได้หลายใบ (1:N) ===== -->
            <template v-else>
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">รายการบริการ (ประมาณการ)</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter">No.</th>
                                <th class="texttablehcenter" style="min-width:240px">ชื่อสินค้า/รายละเอียด</th>
                                <th class="texttablehcenter text-right" style="width:220px">(Rate/Hr.) / Estimate Fees
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in doc.items" :key="row.id">
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    {{ row.name }}
                                    <div v-if="row.detail" class="caption blue--text multiline-detail">{{ row.detail }}
                                    </div>
                                </td>
                                <td class="text-right">{{ row.rate }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>

                <v-row class="mt-4">
                    <v-col cols="12" md="6">
                        <v-text-field :value="doc.not_to_exceed_cap || 'ไม่ระบุ'"
                            label="วงเงินไม่เกิน (Not-to-Exceed Cap)" outlined dense background-color="#F5F5F5"
                            disabled></v-text-field>
                    </v-col>
                </v-row>

                <div class="wf-alert wf-alert-info">
                    ℹ️ ไม่มีการคำนวณ Grand Total อัตโนมัติ — ยอดเรียกเก็บจริงคำนวณตอนออก Invoice แต่ละรอบ
                </div>
            </template>

            <v-row class="mt-2 mb-2">
                <v-col align="right" cols="12">
                    <v-btn outlined color="#1565C0" @click="onPrint">
                        🖨️ Print / PDF
                    </v-btn>
                </v-col>
            </v-row>

            <!-- ===== Invoice ที่ผูกกับเอกสารนี้ — ใช้ตารางเดียวกันทั้ง Quotation (1:1) และ Fee Estimate (1:N) ===== -->
            <v-card outlined class="mt-4 pa-4">
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">เอกสารนี้ถูกแปลงเป็นใบแจ้งหนี้</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter">Invoice No.</th>
                                <th class="texttablehcenter">Date</th>
                                <th class="texttablehcenter">รอบบิล</th>
                                <th class="texttablehcenter text-right">Amount</th>
                                <th class="texttablehcenter">Status</th>
                                <th class="texttablehcenter">Preview</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="inv in doc.linked_invoices" :key="inv.invoice_no">
                                <td class="text-center">{{ inv.invoice_no }}</td>
                                <td class="text-center">{{ inv.date }}</td>
                                <td class="text-center">{{ inv.period }}</td>
                                <td class="text-right">{{ fmt(inv.amount) }}</td>
                                <td class="text-center">
                                    <v-chip x-small :color="inv.status === 'Paid' ? 'green' : 'blue'" dark>{{ inv.status
                                        }}</v-chip>
                                </td>
                                <td class="text-center">
                                    <a href="#" @click.prevent="onViewInvoice(inv.id, inv.status)">ดูใบแจ้งหนี้</a>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
                <div class="text-right caption grey--text mt-2">
                    ยอดเรียกเก็บสะสมจากทุก Invoice จนถึงปัจจุบัน:
                    <b class="black--text">{{ fmt(cumulativeInvoiced) }}</b>
                </div>

                <!-- Note 17: เทียบยอดสะสมกับวงเงินไม่เกิน เฉพาะตอนมีการระบุไว้ตอนสร้างเอกสาร (Fee Estimate เท่านั้น) -->
                <div v-if="hasCap" class="mt-2"
                    :class="capExceeded ? 'wf-alert wf-alert-warn' : 'wf-alert wf-alert-info'">
                    <span v-if="capExceeded">⚠️ ยอดเรียกเก็บสะสมเกินวงเงินไม่เกินที่กำหนดไว้แล้ว</span>
                    <span v-else>ℹ️ เทียบกับวงเงินไม่เกิน</span>
                    : {{ fmt(cumulativeInvoiced) }} / {{ fmt(capAmount) }}
                    (คงเหลือ {{ fmt(Math.max(capAmount - cumulativeInvoiced, 0)) }})
                </div>
            </v-card>

            <br />
            <hr />
            <br />
            <div>
                <v-row>
                    <v-col align="center" cols="12" md="12">
                        <v-btn outlined class="mr-5" @click="getBackList()">
                            Back
                        </v-btn>
                        <v-btn class="mr-5 buttonblue" disabled>
                            Save Draft
                        </v-btn>
                        <v-btn depressed class="mr-5 buttonblue" disabled>
                            Submit
                        </v-btn>
                    </v-col>
                </v-row>
                <!-- ไม่มีปุ่ม Delete ตาม REQ-2 — สถานะ Invoiced/InProgress ล็อกทั้งหมด -->
            </div>
        </v-card>
        <v-dialog v-model="dialogSelectTitle" scrollable max-width="35%">
            <v-card class="pa-4">

                <v-card-text>
                    <div class="d-flex align-center mb-0 mt-5">
                        <span class="text-h5 font-weight-bold">Select Company</span>
                    </div>

                    <v-radio-group v-model="dialogSelectClientInfo" column>
                        <v-radio label="DEJ-UDOM & ASSOCIATES LIMITED" value="1" class="mb-2"></v-radio>
                        <v-radio label="สำนักงานกฎหมาย เดชอุดม แอนด์แอสโซซิเอทส์" value="3" class="mb-2"></v-radio>
                    </v-radio-group>
                </v-card-text>

                <v-card-actions style="margin-top: -30px;">
                    <v-row no-gutters>
                        <v-col cols="6" class="pr-2">
                            <v-btn color="#106a94" large rounded dark elevation="0" @click="confirmPrint" block>
                                Print Preview
                            </v-btn>
                        </v-col>
                        <v-col cols="6" class="pl-2">
                            <v-btn color="#fc1526" large rounded dark elevation="0" @click="dialogSelectTitle = false"
                                block>
                                Cancel
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import Swal from "sweetalert2";
import pdfQuotationGenerator from "@/utils/pdfQuotationGenerator";

export default {
    name: "QuotationView",
    data() {
        return {
            print_header_list: [
                {
                    id: "1", // ตรงกับ <v-radio value="1"> (DEJ-UDOM & ASSOCIATES LIMITED)
                    com_title: "STATEMENT",
                    com_name: "",
                    inv_company_name_th: "",
                    inv_company_address: "",
                    inv_company_branch: "",
                    inv_company_tel: "",
                    inv_company_email: "",
                    inv_tax_id: "",
                    com_eng_1: "DEJ-UDOM & ASSOCIATES LTD.",
                    com_eng_2: "ATTORNEYS AT LAW",
                    com_thai: "บริษัท เดชอุดม แอนด์ แอสโซซิเอทส์ จำกัด",
                    com_add_name: "CHARN ISSARA TOWER 1, 2nd FLOOR,",
                    com_add_no: "942/69 RAMA IV ROAD,",
                    com_add_city: "KWAENG SURIYAWONG, KHET BANGRAK,",
                    com_add_country: "BANGKOK 10500, THAILAND",
                    com_add_tel: "Tel : 0-2233-0055, 0-2267-5152",
                    com_add_fax: "Fax : 0-2236-6681, 0-2233-0227",
                    com_add_email: "E-MAIL : account@dejudom.com",
                    com_add_tax_id: `TAX ID. No. 0105534015348/"HEAD OFFICE"`,
                    // ✅ เพิ่ม: ชื่อบริษัทแบบ Title Case ไว้ใช้แทรกในข้อความ Quotation Terms
                    // ให้ตรงกับหัวเอกสารที่เลือกจริง (ต้องมี "Ltd." ต่อท้ายสำหรับบริษัทนี้)
                    com_display_name: "Dej-Udom & Associates Ltd.",
                    customer_paymentterm: "0",
                    credit_term_date: "",
                },
                {
                    id: "3", // ตรงกับ <v-radio value="3"> (สำนักงานกฎหมาย เดชอุดม แอนด์แอสโซซิเอทส์)
                    com_title: "STATEMENT",
                    com_name: "",
                    inv_company_name_th: "",
                    inv_company_address: "",
                    inv_company_branch: "",
                    inv_company_tel: "",
                    inv_company_email: "",
                    inv_tax_id: "",
                    com_eng_1: "DEJ-UDOM & ASSOCIATES",
                    com_eng_2: "ATTORNEYS AT LAW",
                    com_thai: "สำนักกฎหมาย เดชอุดม แอนด์ แอสโซซิเอทส์",
                    com_add_name: "CHARN ISSARA TOWER 1, 2nd FLOOR,",
                    com_add_no: "942/69 RAMA IV ROAD,",
                    com_add_city: "KWAENG SURIYAWONG, KHET BANGRAK,",
                    com_add_country: "BANGKOK 10500, THAILAND",
                    com_add_tel: "Tel : 0-2233-0055, 0-2267-5152",
                    com_add_fax: "Fax : 0-2236-6681, 0-2233-0227",
                    com_add_email: "E-MAIL : account@dejudom.com",
                    com_add_tax_id: `TAX ID. No. 0992000028227/"HEAD OFFICE"`,
                    // ✅ เพิ่ม: สำนักงานนี้ไม่มี "Ltd." ต่อท้าย ต้องตรงกับหัวเอกสาร (com_eng_1 ด้านบน)
                    com_display_name: "Dej-Udom & Associates",
                    customer_paymentterm: "0",
                    credit_term_date: "",
                },
            ],
            dialogSelectTitle: false,
            dialogSelectClientInfo: "1",
            doc: {
                doc_no: "",
                doc_type: "fixed",
                status: "Invoiced",
                doc_date_show: "",
                client_code: "",
                client_name: "",
                client_address: "",
                tax_id: "",
                branch: "",
                payment_term: "0",
                currency: "THB",
                vat_percent: "7.00",
                not_to_exceed_cap: "",
                items: [],
                linked_invoices: [],
            },
            clientOptions: [],
        };
    },
    computed: {
        statusLabel() {
            return { Invoiced: "Invoiced", InProgress: "In Progress" }[this.doc.status] || this.doc.status;
        },
        statusColor() {
            return { Invoiced: "green", InProgress: "purple" }[this.doc.status] || "grey";
        },
        fixedTotals() {
            const total = (this.doc.items || []).reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.price) || 0), 0);
            const vat = total * (Number(this.doc.vat_percent) / 100 || 0);
            return { total, vat, grandTotal: total + vat };
        },
        cumulativeInvoiced() {
            return (this.doc.linked_invoices || []).reduce((s, inv) => s + inv.amount, 0);
        },
        // Note 17: เทียบยอดสะสมกับวงเงินไม่เกิน — แสดงเฉพาะตอนมีการระบุไว้ตอนสร้างเอกสารเท่านั้น
        hasCap() {
            const raw = (this.doc.not_to_exceed_cap || "").toString().replace(/,/g, "").trim();
            return raw !== "" && Number(raw) > 0;
        },
        capAmount() {
            const raw = (this.doc.not_to_exceed_cap || "").toString().replace(/,/g, "").trim();
            return Number(raw) || 0;
        },
        capExceeded() {
            return this.hasCap && this.cumulativeInvoiced > this.capAmount;
        },
    },
    async mounted() {
        try {
            await api.checkVersion();

            this.userId = localStorage.getItem(server.USER_ID);
            this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

            if (this.authorize_id == null || this.authorize_id == 0) {
                this.$store.state.global_dialog = true;
                this.setupAlertDialog(
                    true,
                    "Authorize Failed!!!",
                    "Please Logout And Login Again!!!",
                    "text-h5 red--text text-center"
                );
                this.$router.back();
                return;
            }

            const parser = document.createElement("a");
            parser.href = window.location;
            const routerPath = parser.pathname.split("/")[1];

            const resAuth = await api.getAuthorize(this.userId, routerPath);
            const permission = resAuth.data[0];

            if (!permission) {
                this.$router.back();
                return;
            }

            this.authorize_view = permission.smd_view >= 1;

            if (!this.authorize_view) {
                this.$router.back();
                return;
            }

            if (this.userId && api.isLoggedIn()) {
                // already logged in
            } else {
                this.$store.state.isLogged = false;
                this.$router.push("/login");
                return;
            }
        } catch (error) {
            console.error("QuotationView mounted() auth error:", error);
            this.$store.state.isLogged = false;
            this.$router.push("/login");
            return;
        }

        // ===== โหลดข้อมูลเอกสารจริง แยก catch ออกจาก auth =====
        this.$showLoader();
        try {
            await this.loadCustomer();
            await this.loadQuotation();
            if (this.doc.status == 'Invoiced') {
                await this.loadDataInvoice();
            } else {
                this.linked_invoices = [];
            }
        } catch (error) {
            console.error("QuotationView mounted() load data error:", error);
            await Swal.fire({
                icon: "error",
                title: "เกิดข้อผิดพลาด",
                text: "ไม่สามารถโหลดข้อมูลเอกสารได้ กรุณาลองใหม่อีกครั้ง",
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
        } finally {
            this.$hideLoader();
        }
    },
    methods: {
        async loadCustomer() {
            const result = await api.getAllCustomerListactiveCompany({
                params: {
                    company_id: localStorage.getItem(server.COMPANYID),
                },
            });
            this.clientOptions = result.data.map((item) => ({
                id: item.id,
                code: item.cust_account,
                name: item.name,
                address: item.address,
                tax_id: item.taxid,
                branch: item.branch,
            }));
        },
        async loadQuotation() {
            const id = this.$route.params.id;

            try {
                const res = await api.billing_expense_getAllQuotationsByID(id);
                const record = res.data;

                if (!record) {
                    await Swal.fire({
                        icon: "error",
                        title: "ไม่พบเอกสาร",
                        text: "ไม่พบเอกสารที่ต้องการดู",
                        showConfirmButton: false,
                        timer: 2500,
                        timerProgressBar: true,
                    });
                    this.$router.push("/quotation-list");
                    return;
                }

                const clientMatch = this.clientOptions.find((c) => c.id === record.client_id);

                const items = record.doc_type === "fixed"
                    ? (record.items || []).map((item) => ({
                        id: item.item_row_id,
                        name: item.item_name,
                        detail: item.detail || "",
                        qty: item.qty != null ? Number(item.qty) : 0,
                        unit: item.unit || "",
                        price: item.unit_price != null ? Number(item.unit_price) : 0,
                    }))
                    : (record.items || []).map((item) => ({
                        id: item.item_row_id,
                        name: item.item_name,
                        detail: item.detail || "",
                        rate: item.line_total != null ? this.fmt(item.line_total) : "",
                    }));

                this.doc = {
                    doc_no: record.doc_no,
                    doc_type: record.doc_type,
                    status: record.status,
                    doc_date: record.doc_date, // ✅ เพิ่ม — เก็บดิบไว้ใช้ตอน print (onPrint ใช้ this.doc.doc_date)
                    doc_date_show: this.formatDocDateShow(record.doc_date),
                    client_code: clientMatch ? clientMatch.code : "",
                    client_name: clientMatch ? clientMatch.name : `Client #${record.client_id}`,
                    client_address: record.client_address,
                    tax_id: clientMatch ? clientMatch.tax_id : "",
                    branch: clientMatch ? clientMatch.branch : "",
                    payment_term: String(record.payment_term ?? "0"),
                    currency: record.currency,
                    vat_percent: String(record.vat_percent ?? "0"),
                    attention: record.attention, // ✅ เพิ่ม — ตกหล่นไปก่อนหน้านี้
                    contact_position: record.contact_position, // ✅ เพิ่ม เผื่อใช้ในอนาคต
                    po_no: record.po_no, // ✅ เพิ่ม — ตกหล่นไปก่อนหน้านี้
                    po_date: record.po_date,
                    delivery_date: record.delivery_date,
                    additional_info: record.additional_info,
                    not_to_exceed_cap: record.not_to_exceed_cap != null ? String(record.not_to_exceed_cap) : "",
                    items: items,
                    // ⚠️ ยังไม่มี API สำหรับดึง invoice ที่ผูกกับเอกสารนี้ — ปล่อยว่างไปก่อน
                    linked_invoices: [],
                };

            } catch (error) {
                console.error("loadQuotation error:", error);
                await Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "ไม่สามารถโหลดข้อมูลเอกสารได้",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                this.$router.push("/quotation-list");
            }
        },
        async loadDataInvoice() {
            const id = this.$route.params.id;
            try {
                const res_inv = await api.billing_expense_loaddata_quottions_invocie(Number(id));
                const invoices = res_inv.data || [];

                this.doc.linked_invoices = invoices.map((inv) => ({
                    id: inv.id,
                    invoice_no: inv.inv_id,
                    date: inv.inv_date,
                    period: inv.billing_period, // ยังไม่มีข้อมูล "รอบบิล" จาก API ตอนนี้ ใส่ placeholder ไว้ก่อน
                    amount: Number(inv.sub_total) || 0,
                    status: inv.inv_status,
                }));
            } catch (error) {
                console.error("loadDataInvoice error:", error);
                this.doc.linked_invoices = [];
            }
        },
        formatDocDateShow(dateOnly) {
            if (!dateOnly) return "";
            const [yyyy, mm, dd] = dateOnly.split("-");
            if (!yyyy || !mm || !dd) return "";
            return `${dd}/${mm}/${yyyy}`;
        },
        getBackList() {
            this.$router.push("/quotation-list");
        },
        fmt(n) {
            return (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        onViewInvoice(id, inv_status) {
            if (inv_status == "Invoice" || inv_status == "P.invoice") {
                const routeData = this.$router.resolve(`/edit-invoice/${id}/inv_h/edit`);
                window.open(routeData.href, "_blank");
            } else if (inv_status == "Receive") {
                const routeData = this.$router.resolve(`/edit-invoice/${id}/inv_h/edit`);
                window.open(routeData.href, "_blank");
            } else {
                return;
            }
        },
        async onPrint() {
            this.dialogSelectTitle = true;
        },
        async confirmPrint() {
            try {
                // เลือกข้อมูลบริษัทจาก print_header_list ตาม id ที่เลือกในไดอะล็อก
                // (fallback เป็นตัวแรกถ้าหาไม่เจอ กัน dialogSelectClientInfo ค้างค่าผิด)
                const selectedCompany =
                    this.print_header_list.find((c) => c.id === this.dialogSelectClientInfo) ||
                    this.print_header_list[0];

                const header = {
                    company_inv: "L", // ใช้ "L" เสมอเพราะเรากำหนด com_eng_1/com_thai/com_add_* เองครบแล้ว
                    com_eng_1: selectedCompany.com_eng_1,
                    com_eng_2: selectedCompany.com_eng_2,
                    com_thai: selectedCompany.com_thai,
                    com_add_name: selectedCompany.com_add_name,
                    com_add_no: selectedCompany.com_add_no,
                    com_add_city: selectedCompany.com_add_city,
                    com_add_country: selectedCompany.com_add_country,
                    com_add_tel: selectedCompany.com_add_tel,
                    com_add_email: selectedCompany.com_add_email,
                    com_add_tax_id: selectedCompany.com_add_tax_id,
                    // ✅ เพิ่ม: ใช้แทรกในข้อความ Quotation Terms เริ่มต้น ให้ตรงกับบริษัทที่เลือกจริง
                    company_display_name: selectedCompany.com_display_name,
                    doc_no: this.doc.doc_no,
                    doc_date: this.doc.doc_date,
                    doc_type: this.doc.doc_type,
                    customer_name: this.doc.client_name,
                    customer_address: this.doc.client_address,
                    customer_taxid: this.doc.tax_id,
                    attention: this.doc.attention,
                    payment_term: this.doc.payment_term,
                    currency: this.doc.currency,
                    subtotal: this.fixedTotals ? this.fixedTotals.total : 0,
                    vat_percent: this.doc.vat_percent,
                    vat_amount: this.fixedTotals ? this.fixedTotals.vat : 0,
                    grand_total: this.fixedTotals ? this.fixedTotals.grandTotal : 0,
                    amount_in_words: "",
                    not_to_exceed_cap: this.doc.not_to_exceed_cap,
                };

                const items = this.doc.items.map((row) => ({
                    description: row.name,
                    detail: row.detail,
                    amount: this.doc.doc_type === "fixed" ? (row.qty || 0) * (row.price || 0) : undefined,
                    rate: this.doc.doc_type === "estimate" ? row.rate : undefined,
                }));

                await pdfQuotationGenerator.exportToPDF({ header, items });
            } catch (error) {
                console.error("❌ เกิดข้อผิดพลาดในการสร้าง PDF:", error);
                alert("เกิดข้อผิดพลาดในการสร้าง PDF: " + error.message);
            }
        },


    },
    async beforeCreate() {
        this.$store.state.navMenu = true;
    },

};
</script>

<style scoped>
.bg-colorth {
    background-color: #595959;
}

.texttablehcenter {
    color: #fff !important;
    text-align: center;
}

.heightsize70formadvance {
    height: 70px;
}

.heightsize55formadvance {
    height: 55px;
}

.v-btn:not(.v-btn--round).v-size--default.buttonblue {
    background-color: #1d2442;
    color: white;
}

.wf-alert {
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
}

.wf-alert-info {
    background: #e3f2fd;
    color: #0d47a1;
    border-left: 4px solid #1565c0;
}

.wf-alert-warn {
    background: #fff3e0;
    color: #e65100;
    border-left: 4px solid #ef6c00;
}

.wf-alert-success {
    background: #e8f5e9;
    color: #1b5e20;
    border-left: 4px solid #2e7d32;
}

.multiline-detail {
    white-space: pre-line;
}
</style>